/**
 * Handles multipart uploads for large files
 */

// Define the size of each chunk in bytes
// Increased from 5MB to 25MB for more efficient uploads
const CHUNK_SIZE = 25 * 1024 * 1024; // 25MB

interface UploadPart {
  PartNumber: number;
  ETag: string;
}

interface MultipartUploadOptions {
  serverUrl: string;
  appId: string;
  userId: string;
  blobId: string;
  version: string;
  file: File;
  onProgress?: (progress: number) => void;
  retryAttempts?: number;
  retryDelay?: number;
}

interface UploadResult {
  success: boolean;
  location?: string;
  error?: string;
}

/**
 * Uploads a file using multipart upload
 */
export async function uploadFileMultipart({
  serverUrl,
  appId,
  userId,
  blobId,
  version,
  file,
  onProgress,
  retryAttempts = 3,
  retryDelay = 1000
}: MultipartUploadOptions): Promise<UploadResult> {
  try {
    // Step 1: Initialize the multipart upload
    const initResponse = await fetch(
      `${serverUrl}/api/blobs/start/${appId}/${userId}/${blobId}/${version}`,
      {
        method: "POST",
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          size: file.size,
          type: file.type,
        })
      }
    );

    if (!initResponse.ok) {
      const errorText = await initResponse.text();
      throw new Error(`Failed to initialize upload: ${errorText}`);
    }

    const { uploadId } = await initResponse.json();

    if (!uploadId) {
      throw new Error("No upload ID received");
    }

    // Step 2: Calculate the total number of parts
    const fileSize = file.size;
    const totalParts = Math.ceil(fileSize / CHUNK_SIZE);
    const uploadedParts: UploadPart[] = [];

    // Step 3: Upload each part
    let uploadedBytes = 0;

    for (let partNumber = 1; partNumber <= totalParts; partNumber++) {
      const start = (partNumber - 1) * CHUNK_SIZE;
      const end = Math.min(start + CHUNK_SIZE, fileSize);
      const chunk = file.slice(start, end);
      const chunkSize = chunk.size;

      let currentAttempt = 0;
      let partUploaded = false;

      while (!partUploaded && currentAttempt < retryAttempts) {
        try {
          currentAttempt++;

          const partResponse = await fetch(
            `${serverUrl}/api/blobs/part/${appId}/${userId}/${blobId}/${version}/${uploadId}/${partNumber}`,
            {
              method: "POST",
              body: chunk,
              headers: {
                'Content-Type': 'application/octet-stream',
              },
            }
          );

          if (!partResponse.ok) {
            const errorText = await partResponse.text();
            throw new Error(`Failed to upload part ${partNumber}: ${errorText}`);
          }

          const partResult = await partResponse.json();
          uploadedParts.push({
            PartNumber: partNumber,
            ETag: partResult.ETag
          });

          partUploaded = true;
          uploadedBytes += chunkSize;

          // Report progress
          if (onProgress) {
            onProgress(uploadedBytes / fileSize);
          }
        } catch (error) {
          if (currentAttempt >= retryAttempts) {
            throw error;
          }

          // Wait before retrying
          await new Promise(resolve => setTimeout(resolve, retryDelay));
        }
      }
    }

    // Step 4: Complete the multipart upload
    const completeResponse = await fetch(
      `${serverUrl}/api/blobs/complete/${appId}/${userId}/${blobId}/${version}/${uploadId}`,
      {
        method: "POST",
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ parts: uploadedParts })
      }
    );

    if (!completeResponse.ok) {
      const errorText = await completeResponse.text();
      throw new Error(`Failed to complete upload: ${errorText}`);
    }

    const completeResult = await completeResponse.json();

    if (!completeResult.complete) {
      // Handle missing parts
      if (completeResult.missingParts && completeResult.missingParts.length > 0) {
        const missingPartsList = completeResult.missingParts.join(', ');
        throw new Error(`Upload incomplete. Missing parts: ${missingPartsList}`);
      }
      throw new Error("Upload could not be completed");
    }

    return {
      success: true,
      location: completeResult.location
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : String(error)
    };
  }
}

/**
 * Aborts a multipart upload
 */
export async function abortMultipartUpload(
  serverUrl: string,
  appId: string,
  userId: string,
  blobId: string,
  version: string,
  uploadId: string
): Promise<boolean> {
  try {
    const abortResponse = await fetch(
      `${serverUrl}/api/blobs/abort/${appId}/${userId}/${blobId}/${version}/${uploadId}`,
      { method: "POST" }
    );

    if (!abortResponse.ok) {
      const errorText = await abortResponse.text();
      throw new Error(`Failed to abort upload: ${errorText}`);
    }

    const result = await abortResponse.json();
    return result.aborted === true;
  } catch (error) {
    return false;
  }
}

/**
 * Resume an interrupted multipart upload
 */
export async function resumeMultipartUpload({
  serverUrl,
  appId,
  userId,
  blobId,
  version,
  uploadId,
  file,
  onProgress
}: MultipartUploadOptions & { uploadId: string }): Promise<UploadResult> {
  try {
    // First, list the parts that have already been uploaded
    const listResponse = await fetch(
      `${serverUrl}/api/blobs/list-parts/${appId}/${userId}/${blobId}/${version}/${uploadId}`,
      { method: "GET" }
    );

    if (!listResponse.ok) {
      const errorText = await listResponse.text();
      throw new Error(`Failed to list existing parts: ${errorText}`);
    }

    const { parts: existingParts } = await listResponse.json();
    const uploadedPartNumbers = new Set((existingParts || []).map((p: any) => p.PartNumber));

    // Calculate total parts
    const fileSize = file.size;
    const totalParts = Math.ceil(fileSize / CHUNK_SIZE);
    const uploadedParts = [...existingParts]

    // Calculate already uploaded bytes
    let uploadedBytes = 0;
    for (const part of existingParts) {
      uploadedBytes += part.Size || 0;
    }

    // Upload missing parts
    for (let partNumber = 1; partNumber <= totalParts; partNumber++) {
      if (uploadedPartNumbers.has(partNumber)) {
        continue; // Skip already uploaded parts
      }

      const start = (partNumber - 1) * CHUNK_SIZE;
      const end = Math.min(start + CHUNK_SIZE, fileSize);
      const chunk = file.slice(start, end);
      const chunkSize = chunk.size;

      const partResponse = await fetch(
        `${serverUrl}/api/blobs/part/${appId}/${userId}/${blobId}/${version}/${uploadId}/${partNumber}`,
        {
          method: "POST",
          body: chunk,
          headers: {
            'Content-Type': 'application/octet-stream',
          }
        }
      );

      if (!partResponse.ok) {
        const errorText = await partResponse.text();
        throw new Error(`Failed to upload part ${partNumber}: ${errorText}`);
      }

      const partResult = await partResponse.json();
      uploadedParts.push({
        PartNumber: partNumber,
        ETag: partResult.ETag
      });

      uploadedBytes += chunkSize;

      // Report progress
      if (onProgress) {
        onProgress(uploadedBytes / fileSize);
      }
    }

    // Complete the upload
    return await completeUpload(serverUrl, appId, userId, blobId, version, uploadId, uploadedParts);
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : String(error)
    };
  }
}

async function completeUpload(
  serverUrl: string,
  appId: string,
  userId: string,
  blobId: string,
  version: string,
  uploadId: string,
  parts: UploadPart[]
): Promise<UploadResult> {
  const completeResponse = await fetch(
    `${serverUrl}/api/blobs/complete/${appId}/${userId}/${blobId}/${version}/${uploadId}`,
    {
      method: "POST",
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ parts })
    }
  );

  if (!completeResponse.ok) {
    const errorText = await completeResponse.text();
    throw new Error(`Failed to complete upload: ${errorText}`);
  }

  const result = await completeResponse.json();

  if (!result.complete) {
    return {
      success: false,
      error: `Upload incomplete. Missing parts: ${(result.missingParts || []).join(', ')}`
    };
  }

  return {
    success: true,
    location: result.location
  };
}

// Utility functions removed as they were only used for logging
